import { useEffect, useState } from 'react';
import { getMyEntitlementsApiCall } from '@guryou/api-client-gql';

// Lightweight shared-cache read of the current provider's entitlements
// (plan ⊕ add-ons ⊕ override). One fetch per session, shared across components.
// NOTE: read-only — no enforcement here. Returns null while loading.

const EMPTY = { planId: 0, booleans: {}, limits: {}, meters: {} };
let cache = null;
let inflight = null;
const listeners = new Set();

const parse = v => {
  try {
    return typeof v === 'string' ? JSON.parse(v) : v || {};
  } catch {
    return {};
  }
};

const fetchOnce = () => {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = getMyEntitlementsApiCall()
      .then(r => {
        const d = r && r.success ? r.data : null;
        cache = d ? { planId: d.planId, booleans: parse(d.booleans), limits: parse(d.limits), meters: parse(d.meters) } : { ...EMPTY };
        listeners.forEach(l => l(cache));
        return cache;
      })
      .catch(() => {
        cache = { ...EMPTY };
        listeners.forEach(l => l(cache));
        return cache;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
};

export const refreshEntitlements = () => {
  cache = null;
  return fetchOnce();
};

export const useEntitlements = () => {
  const [ent, setEnt] = useState(cache);
  useEffect(() => {
    let mounted = true;
    const l = c => mounted && setEnt(c);
    listeners.add(l);
    fetchOnce();
    return () => {
      mounted = false;
      listeners.delete(l);
    };
  }, []);
  return ent; // null while loading
};

// Boolean capability check. Returns false while loading (fail-safe for UI gating).
export const useCan = featureKey => {
  const ent = useEntitlements();
  return !!(ent && ent.booleans && ent.booleans[featureKey]);
};

// Numeric limit (or null if unset / loading).
export const useLimit = featureKey => {
  const ent = useEntitlements();
  return ent && ent.limits && Object.prototype.hasOwnProperty.call(ent.limits, featureKey) ? ent.limits[featureKey] : null;
};

// Meter balance { unit, balance } (or null).
export const useMeter = featureKey => {
  const ent = useEntitlements();
  return ent && ent.meters ? ent.meters[featureKey] || null : null;
};
